import { useGetVehicles } from "@/entities/get-vehicles/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import { useAuth } from "@/shared/auth/fetch/useAuth";
import { transformLoadable } from "@/shared/lib/loadable";
import { loadableFromUseQueryResult } from "@/shared/lib/tanstack-query";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import type {
  MasterDataVehicle,
  MasterDataVehicleListPageProps,
} from "../model";

export const useMasterDataVehicleListPageProps =
  (): MasterDataVehicleListPageProps => {
    // Auth
    const auth = useAuth();

    // State
    const [getVehiclesParams, setGetDraftOffersCargosParams] =
      useState<
        Parameters<
          MasterDataVehicleListPageProps["onGetVehiclesParamsChange"]
        >[0]
      >();
    const [vehiclesPagination, setVehiclesPagination] = useState<{
      page: number;
      perPage: number;
    }>({
      page: 1,
      perPage: 50,
    });

    // Local Storage
    // 車両登録画面に遷移する際に、記入途中の情報をLocalStorageから削除する
    const [_, __, removeMasterDataVehicleCreatePageInProgressValues] =
      useInProgressValuesLocalCache<unknown>("MasterDataVehicleCreatePage");
    // 車両詳細画面に遷移する際に、記入途中の情報をLocalStorageから削除する
    const [___, ____, removeMasterDataVehicleDetailPageInProgressValues] =
      useInProgressValuesLocalCache<unknown>("MasterDataVehicleDetailPage");

    // Backend API
    const getVehicles = useGetVehicles(
      {
        search: {
          ...getVehiclesParams,
          companyId: auth.session.isAuthenticated
            ? auth.session.user.companyId
            : -1,
        },
        pagination: {
          pageNumber: vehiclesPagination.page,
          displayLimit: vehiclesPagination.perPage,
        },
      },
      {
        enabled: auth.session.isAuthenticated,
      },
    );

    // Props
    const vehicles = useMemo<MasterDataVehicleListPageProps["vehicles"]>(() => {
      return transformLoadable(
        loadableFromUseQueryResult(getVehicles),
        (data) => {
          const vehiclesData = data.data.vehicles.map<MasterDataVehicle>(
            (vehicle) => {
              const id = vehicle.id;
              if (!id) {
                throw new Error(
                  `車両IDが不正な値です。 ${JSON.stringify(vehicle)}`,
                );
              }
              return {
                ...vehicle,
                id,
              };
            },
          );
          return {
            data: vehiclesData,
            pagination: {
              ...vehiclesPagination,
              totalCount: data.data.totalCount,
              onPageChange: (page) =>
                setVehiclesPagination((prev) => ({ ...prev, page })),
              onPerPageChange: (perPage) =>
                setVehiclesPagination((prev) => ({ ...prev, perPage })),
            },
          };
        },
      );
    }, [vehiclesPagination, getVehicles]);

    // Actions
    const navigate = useNavigate();
    const onCreateVehicleClick: MasterDataVehicleListPageProps["onCreateVehicleClick"] =
      () => {
        removeMasterDataVehicleCreatePageInProgressValues();
        navigate({ to: "/master-data/vehicles/create" });
      };
    const onShowVehicleClick: MasterDataVehicleListPageProps["onShowVehicleClick"] =
      (vehicleId) => {
        removeMasterDataVehicleDetailPageInProgressValues();
        navigate({ to: `/master-data/vehicles/${vehicleId}` });
      };
    const onGetVehiclesParamsChange: MasterDataVehicleListPageProps["onGetVehiclesParamsChange"] =
      (filter) => {
        setGetDraftOffersCargosParams(filter);
      };
    const onGetVehiclesCargosParamsReset: MasterDataVehicleListPageProps["onGetVehiclesCargosParamsReset"] =
      () => {
        setGetDraftOffersCargosParams(undefined);
      };

    return {
      vehicles,
      onCreateVehicleClick,
      onShowVehicleClick,
      onGetVehiclesParamsChange,
      onGetVehiclesCargosParamsReset,
    };
  };
